import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import * as monthUtils from '@actual-app/core/shared/months';

import { useEnvelopeSheetValue } from '#components/budget/envelope/EnvelopeBudgetComponents';
import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import {
  datosRumbo,
  hayDineroParaRumbo,
  vistaRumbo,
} from '#components/mobile/travesia/pasoRumbo';
import type { DatosRumbo } from '#components/mobile/travesia/pasoRumbo';
import { Boton } from '#components/mobile/ui/Boton';
import { color } from '#components/mobile/ui/tokens';
import { useCurrentAccess } from '#hooks/useCurrentAccess';
import { useLocalPref } from '#hooks/useLocalPref';
import { usePlan } from '#hooks/usePlan';
import { SheetNameProvider } from '#hooks/useSheetName';
import { envelopeBudget } from '#spreadsheet/bindings';

import {
  estiloCampo,
  Etiqueta,
  HojaTravesia,
  TituloHoja,
  useEuros,
  useTecladoAbierto,
  Z_TRAVESIA,
} from './comun';

type Fase = 'presenta' | 'crear' | 'creada' | 'mes' | 'cierre';

type PasoRumboProps = {
  /** Id de la categoría Rumbo si ya se creó (al volver con «Atrás»). */
  idRumbo: string | undefined;
  ocupado?: boolean;
  /** Crea (o reutiliza) la categoría; puede fallar (el paso no avanza). */
  onCrear: (nombre: string, datos: DatosRumbo) => Promise<void>;
  /** Aparta el precio, o lo deja pendiente («Aún no tengo dinero»). */
  onApartar: (datos: DatosRumbo, pendiente: boolean) => Promise<void>;
  /** Al paso siguiente. */
  onSeguir: () => void;
  /** El paso no toca (ya paga, sin servidor…): se pasa solo. */
  onNoToca: () => void;
};

/**
 * Paso 6 · «Rumbo también se planifica» (aprobado por Vadik el 1-nov-2026):
 * Gavi presenta la prueba y el precio, la persona crea la categoría Rumbo
 * (grupo Suscripciones), pasa en el Plan real al mes en que acaba la prueba
 * y aparta allí el precio de un mes. Sin dinero sin asignar, lo ve con una
 * cifra de ejemplo que no se guarda. La beta ve una línea; quien ya paga, o
 * sin servidor, se lo salta solo.
 */
export function PasoRumbo({
  idRumbo,
  ocupado,
  onCrear,
  onApartar,
  onSeguir,
  onNoToca,
}: PasoRumboProps) {
  const { t, i18n } = useTranslation();
  const { eur } = useEuros();
  const { plan, cargando } = usePlan();
  const { isAdmin } = useCurrentAccess();
  const vista = vistaRumbo({ plan, cargando, esAdmin: isAdmin });
  const [fase, setFase] = useState<Fase>('presenta');
  const [nombre, setNombre] = useState('Rumbo');
  const [verPrecios, setVerPrecios] = useState(false);
  const [, setMesPlan] = useLocalPref('budget.startMonth');

  // Paso que no toca: se pasa solo, una vez.
  const saltado = useRef(false);
  useEffect(() => {
    if (vista === 'no' && !saltado.current) {
      saltado.current = true;
      onNoToca();
    }
  }, [vista, onNoToca]);

  if (vista === 'cargando' || vista === 'no') {
    return null;
  }

  if (vista === 'beta') {
    return (
      <HojaTravesia
        aria-label={t('Rumbo is planned too')}
        data-testid="hoja-rumbo-beta"
        cabeza={
          <Bocadillo
            variante="hoja"
            estado="aplaude"
            texto={t(
              'You are part of the beta crew: you do not need this step.',
            )}
          />
        }
        pie={
          <Boton
            bloque
            isDisabled={ocupado}
            data-testid="travesia-continuar"
            onPress={onSeguir}
          >
            <Trans>Continue</Trans>
          </Boton>
        }
      >
        <TituloHoja>
          <Trans>Rumbo is planned too</Trans>
        </TituloHoja>
      </HojaTravesia>
    );
  }

  // `vista === 'completo'`: hay precio (vistaRumbo lo comprueba).
  const precios = plan?.precios ?? { mensual: 0, anual: 0 };
  const datos = datosRumbo(
    // El admin lo prueba con hoy + 45 días si no está en su prueba.
    plan?.estado === 'prueba' ? (plan.gratisHasta ?? null) : null,
    precios.mensual,
    monthUtils.currentDay(),
  );
  const fecha = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${datos.fin}T12:00:00`));
  const mesNombre = new Intl.DateTimeFormat(i18n.language, {
    month: 'long',
  }).format(new Date(`${datos.mes}-15T12:00:00`));
  const precio = eur(datos.precio);

  if (fase === 'presenta') {
    return (
      <HojaTravesia
        aria-label={t('Rumbo is planned too')}
        data-testid="hoja-rumbo"
        cabeza={
          <Bocadillo
            variante="hoja"
            estado="reposo"
            texto={t(
              'You have 45 days free, no card: until {{date}}. If you want to keep going afterwards, Rumbo costs {{price}} a month. Let us set it aside early, that is what we are here for.',
              { date: fecha, price: precio },
            )}
          />
        }
        pie={
          <Boton
            bloque
            isDisabled={ocupado}
            data-testid="travesia-continuar"
            onPress={() => setFase(idRumbo ? 'mes' : 'crear')}
          >
            <Trans>Let us go</Trans>
          </Boton>
        }
      >
        <TituloHoja>
          <Trans>Rumbo is planned too</Trans>
        </TituloHoja>
        {verPrecios ? (
          <p
            data-testid="rumbo-precios"
            style={{ margin: 0, fontSize: 15, color: color.fg2 }}
          >
            {t(
              '{{monthly}} a month or {{yearly}} a year, with your partner included. No card while the trial lasts.',
              { monthly: precio, yearly: eur(precios.anual) },
            )}
          </p>
        ) : (
          <Button
            variant="bare"
            data-testid="rumbo-ver-precios"
            onPress={() => setVerPrecios(true)}
            style={{
              alignSelf: 'flex-start',
              minHeight: 44,
              padding: 0,
              fontSize: 15,
              fontWeight: 700,
              color: color.accent,
            }}
          >
            <Trans>See prices</Trans>
          </Button>
        )}
      </HojaTravesia>
    );
  }

  if (fase === 'crear' || fase === 'creada') {
    const creada = fase === 'creada';
    return (
      <HojaTravesia
        aria-label={t('New category')}
        data-testid="hoja-rumbo-crear"
        cabeza={
          <Bocadillo
            variante="hoja"
            estado={creada ? 'aplaude' : 'reposo'}
            texto={
              creada
                ? t(
                    'Done. This is where what Rumbo costs lives, with your money and in your name. It is the only category that makes me a little nervous.',
                  )
                : t('This category is yours, like all of them. I just watch.')
            }
          />
        }
        pie={
          creada ? (
            <Boton
              bloque
              isDisabled={ocupado}
              data-testid="travesia-continuar"
              onPress={() => setFase('mes')}
            >
              <Trans>Continue</Trans>
            </Boton>
          ) : (
            <Boton
              bloque
              isDisabled={ocupado || nombre.trim() === ''}
              data-testid="rumbo-crear"
              onPress={() =>
                void onCrear(nombre, datos)
                  .then(() => setFase('creada'))
                  .catch(() => undefined)
              }
            >
              <Trans>Create category</Trans>
            </Boton>
          )
        }
      >
        <TituloHoja>
          <Trans>New category</Trans>
        </TituloHoja>
        <Etiqueta htmlFor="travesia-rumbo-nombre">
          <Trans>Name</Trans>
        </Etiqueta>
        <input
          id="travesia-rumbo-nombre"
          data-testid="rumbo-nombre"
          value={nombre}
          disabled={creada}
          maxLength={40}
          onChange={e => setNombre(e.target.value)}
          style={{ ...estiloCampo, width: '100%', boxSizing: 'border-box' }}
        />
        <p
          data-testid="rumbo-grupo"
          style={{ margin: '12px 0 0', fontSize: 15, color: color.fg2 }}
        >
          {t('In the group: {{group}}', { group: t('Subscriptions') })}
        </p>
      </HojaTravesia>
    );
  }

  if (fase === 'cierre') {
    return (
      <HojaTravesia
        aria-label={t('Rumbo is planned too')}
        data-testid="hoja-rumbo-cierre"
        cabeza={
          <Bocadillo
            variante="hoja"
            estado="aplaude"
            texto={t(
              'Done. {{date}} will not be a surprise. And now you know how to set money aside in a month that has not arrived yet: it works for any bill.',
              { date: fecha },
            )}
          />
        }
        pie={
          <Boton
            bloque
            isDisabled={ocupado}
            data-testid="travesia-continuar"
            onPress={() => {
              // El Plan vuelve al mes en curso para el objetivo y el reparto.
              setMesPlan(monthUtils.currentMonth());
              onSeguir();
            }}
          >
            <Trans>Continue</Trans>
          </Boton>
        }
      >
        <TituloHoja>
          <Trans>Rumbo is planned too</Trans>
        </TituloHoja>
      </HojaTravesia>
    );
  }

  return (
    <SheetNameProvider
      name={monthUtils.sheetForMonth(monthUtils.currentMonth())}
    >
      <MesRumbo
        idRumbo={idRumbo}
        datos={datos}
        fecha={fecha}
        mesNombre={mesNombre}
        precio={precio}
        ocupado={ocupado}
        onApartar={pendiente =>
          void onApartar(datos, pendiente)
            .then(() => setFase('cierre'))
            .catch(() => undefined)
        }
      />
    </SheetNameProvider>
  );
}

type MesRumboProps = {
  idRumbo: string | undefined;
  datos: DatosRumbo;
  fecha: string;
  mesNombre: string;
  precio: string;
  ocupado?: boolean;
  onApartar: (pendiente: boolean) => void;
};

/**
 * Pantalla 3, sobre el Plan real: Gavi flota encima de la barra, el
 * selector de mes se ilumina hasta que se pasa al mes del fin de la prueba
 * y, allí, la fila de Rumbo. Se aparta con el teclado real (tocando Rumbo)
 * o con el botón del bocadillo. Sin dinero sin asignar (o con «Aún no tengo
 * dinero»), la fila enseña una cifra de ejemplo que no se guarda. Debe ir
 * dentro del `SheetNameProvider` del mes en curso.
 */
function MesRumbo({
  idRumbo,
  datos,
  fecha,
  mesNombre,
  precio,
  ocupado,
  onApartar,
}: MesRumboProps) {
  const { t } = useTranslation();
  const [mesPlan] = useLocalPref('budget.startMonth');
  const enMes = (mesPlan ?? monthUtils.currentMonth()) === datos.mes;
  const teclado = useTecladoAbierto();
  const listo = useEnvelopeSheetValue(envelopeBudget.toBudget);
  const [apartado, setApartado] = useState(0);
  const [ejemplo, setEjemplo] = useState(false);
  // Se decide con el primer valor de «listos para asignar» (la hoja tarda un
  // poco en cargar) y no cambia: al apartar, ese valor baja.
  const [sinDinero, setSinDinero] = useState<boolean | null>(null);
  useEffect(() => {
    if (sinDinero === null && typeof listo === 'number') {
      setSinDinero(!hayDineroParaRumbo(listo, datos.precio));
    }
  }, [listo, sinDinero, datos.precio]);
  const demo = ejemplo || sinDinero === true;
  const hecho = apartado >= datos.precio;

  // Apartado con el teclado real: el paso se da por hecho.
  const avisado = useRef(false);
  useEffect(() => {
    if (hecho && !demo && !avisado.current) {
      avisado.current = true;
      onApartar(false);
    }
  }, [hecho, demo, onApartar]);

  let texto: string;
  let estado: GaviEstado = 'reposo';
  if (!enMes) {
    texto = demo
      ? t(
          'Right now there is not {{price}} unassigned. Let me show you with an example amount: go to {{month}}.',
          { price: precio, month: mesNombre },
        )
      : t(
          'The trial ends on {{date}}. Go to {{month}} and assign {{price}} to Rumbo.',
          { date: fecha, month: mesNombre, price: precio },
        );
  } else if (demo) {
    estado = 'aplaude';
    texto = t(
      'It would look like this: {{price}} set aside in {{month}}. It is an example, nothing is saved. When your first income arrives, I will remind you.',
      { price: precio, month: mesNombre },
    );
  } else {
    texto = t('Tap Rumbo and assign {{price}}, or use the button.', {
      price: precio,
    });
  }

  const foco = !enMes
    ? '[data-testid="selector-mes"]'
    : idRumbo
      ? `[data-category-id="${idRumbo}"]`
      : null;

  return createPortal(
    <>
      {idRumbo && (
        <SheetNameProvider name={monthUtils.sheetForMonth(datos.mes)}>
          <LeerApartado id={idRumbo} onValor={setApartado} />
        </SheetNameProvider>
      )}
      {foco && !teclado && (
        <style>{`${foco}{position:relative;z-index:16;border-radius:8px;box-shadow:0 0 0 3px ${color.accent},0 0 0 9999px ${color.velo}}`}</style>
      )}
      {/* En la hoja de meses, el mes del fin de la prueba se marca. */}
      <style>{`[data-testid="selector-mes-hoja"] [data-month="${datos.mes}"]{box-shadow:0 0 0 3px ${color.accent}!important;opacity:1!important}`}</style>
      {demo &&
        enMes &&
        idRumbo && (
          // La cifra de ejemplo, en la columna de lo asignado (no se guarda).
          <style>{`[data-category-id="${idRumbo}"] [data-testid="budget-cell-amount"]{position:relative;color:transparent!important}[data-category-id="${idRumbo}"] [data-testid="budget-cell-amount"]::after{content:"${precio.replace(/"/g, '')}";position:absolute;right:0;top:50%;transform:translateY(-50%);white-space:nowrap;font-weight:800;color:${color.accent};border-bottom:1.5px dashed ${color.accent}}`}</style>
        )}
      {!teclado && (
        <div
          data-testid="gavi-rumbo"
          style={{
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))',
            zIndex: Z_TRAVESIA.flota,
          }}
        >
          <Bocadillo
            texto={texto}
            estado={estado}
            data-testid="bocadillo-rumbo"
          >
            <div className="acciones" style={{ display: 'grid', gap: 8 }}>
              {enMes && demo && (
                <Boton
                  bloque
                  isDisabled={ocupado}
                  data-testid="rumbo-entendido"
                  onPress={() => onApartar(true)}
                >
                  <Trans>Got it</Trans>
                </Boton>
              )}
              {enMes && sinDinero === false && !ejemplo && (
                <Boton
                  bloque
                  isDisabled={ocupado || hecho}
                  data-testid="rumbo-asignar"
                  onPress={() => onApartar(false)}
                >
                  {t('Assign {{amount}}', { amount: precio })}
                </Boton>
              )}
              {sinDinero === false && !ejemplo && (
                <Boton
                  bloque
                  variante="fantasma"
                  isDisabled={ocupado}
                  data-testid="rumbo-sin-dinero"
                  onPress={() => setEjemplo(true)}
                >
                  <Trans>I do not have money yet</Trans>
                </Boton>
              )}
            </div>
          </Bocadillo>
        </div>
      )}
    </>,
    document.body,
  );
}

/**
 * Lo asignado a Rumbo en el mes del fin de la prueba (céntimos). Va dentro
 * del `SheetNameProvider` de ese mes y avisa en cada cambio.
 */
function LeerApartado({
  id,
  onValor,
}: {
  id: string;
  onValor: (valor: number) => void;
}) {
  const valor = useEnvelopeSheetValue(envelopeBudget.catBudgeted(id));
  const n = typeof valor === 'number' ? valor : 0;
  useEffect(() => {
    onValor(n);
  }, [n, onValor]);
  return null;
}
